import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import cn from "classnames";

import {
    facebookAppId,
    fields,
    languages,
    genders,
    displayNames,
    schoolTypes,
    motherTongues,
} from "../../../common/parameters";
import { profilePictureHost } from "../../paths";
import Seo from "../../../common/helpers/seo";

import NewPassword from "./NewPassword";
import ChangePassword from "./ChangePassword";
import ChangeTextValue from "./ChangeTextValue";
import ChangeNumberValue from "./ChangeNumberValue";
import ChangeSelectValue from "./ChangeSelectValue";
import ChangeRadioValue from "./ChangeRadioValue";
import ChangeCheckboxValue from "./ChangeCheckboxValue";
import DeleteAccountModal from "./DeleteAccountModal";

import ProfileCropper from "../../components/ProfileCropper";
import Modal from "../../components/Modal";
import { Submit, Cancel, Title, Label, FormElement } from "../../components/Form";
import Icon from "../../icon";

import {
    StyledAccount,
    InnerSpring,
    Tick,
    ProfilePicture,
    ModifyProfilePicture,
    ProfilePictureOptions,
    SocialConnections,
    Value,
    VerifiedEmail,
} from "./styles";
import messages from "./messages";
import { IResendEmailValidationProps, IProps } from "./interfaces";
import { useSelector } from "react-redux";
import { IStateTree } from "../../store/interfaces";
import { IUser } from "../../store/user/models";
import { useGoogleLogin } from "@react-oauth/google";
import { getAppFromHistory } from "../../../common/app";
import { useHistory } from "react-router-dom";

const ResendEmailValidation = ({ verificationSuccess, resendVerification }: IResendEmailValidationProps) => (
    <div
        className="link"
        style={{ marginTop: 8, pointerEvents: verificationSuccess ? "none" : undefined }}
        onClick={resendVerification}
    >
        <FormattedMessage {...messages.resendVerification} />
        {verificationSuccess && (
            <Tick>
                <Icon i="tick" />
            </Tick>
        )}
    </div>
);

const AccountView = ({
    newPassword,
    changePassword,
    changeTextModal,
    changingName,
    changingValue,
    modalOpen,
    modalType,
    imageLoaded,
    facebookLoginStarted,
    googleConnectSuccess,
    googleConnectFailure,
    changingLabel,
    changeNumberModal,
    changeSelectModal,
    changeRadioModal,
    changeCheckboxModal,
    setImage,
    cropDone,
    changeSelectValue,
    changeTextValue,
    changeNumberValue,
    changeCheckboxValue,
    facebookConnect,
    removeGoogle,
    changeRadioValue,
    options,
    deleteAccount,
    fbInProgress,
    glInProgress,
    cropping,
    setImageToggle,
    img,
    verificationSuccess,
    resendVerification,
    removeFacebook,
    closeModal,
    onImageLoaded,
    setCropping,
    unloadImage,
    closeChangeTextModal,
    closeChangeNumberModal,
    closeChangeSelectModal,
    closeChangeRadioModal,
    closeChangeCheckboxModal,
    setDeleteAccount,
    setChangePassword,
    setNewPassword,
}: IProps) => {
    const { formatMessage } = useIntl();
    const user = useSelector((state: IStateTree) => state.user as IUser);
    if (!user.profiled && !user.isStudent) return null;

    const handleLoginWithGoogle = useGoogleLogin({
        onSuccess: googleConnectSuccess,
        onError: googleConnectFailure,
    });

    const history = useHistory();
    const app = getAppFromHistory(history);
    const isClassroomTeacher = !app && !user.isStudent;
    
    return (
        <StyledAccount marginTop={50} className={cn("spring--shadow", "spring--padding")}>
            <Seo title={formatMessage(messages.account)} />
            <InnerSpring>
                <h2>
                    <FormattedMessage {...messages.account} />
                </h2>
                {!user.isStudent && (
                    <div>
                        {(!cropping || !imageLoaded) &&
                            (user.profilePicture ? (
                                <ProfilePicture>
                                    <img src={img || profilePictureHost + user.profilePicture + ".jpg"} alt="profile" />
                                </ProfilePicture>
                            ) : (
                                <ProfilePicture>
                                    <Icon i="user" />
                                </ProfilePicture>
                            ))}
                        {cropping && (
                            <ProfileCropper
                                setImageToggle={setImageToggle}
                                setImage={setImage}
                                imageLoaded={onImageLoaded}
                            />
                        )}
                        {!cropping && (
                            <ModifyProfilePicture>
                                <div className={"link"} onClick={() => setCropping(true)}>
                                    <FormattedMessage {...messages.modify} />
                                </div>
                            </ModifyProfilePicture>
                        )}
                        {cropping && imageLoaded && (
                            <ProfilePictureOptions>
                                <Submit onClick={cropDone}>
                                    <FormattedMessage {...messages.uploadPicture} />
                                </Submit>
                                <Cancel
                                    onClick={() => {
                                        setCropping(false);
                                        unloadImage();
                                    }}
                                >
                                    <FormattedMessage {...messages.cancel} />
                                </Cancel>
                            </ProfilePictureOptions>
                        )}
                    </div>
                )}

                {!user.isStudent && (
                    <SocialConnections>
                        {user.facebook?.connected ? (
                            user.password ? (
                                <Submit facebook onClick={removeFacebook} disabled={fbInProgress}>
                                    <span className={"button-facebook"}>
                                        <Icon i="facebook" />
                                    </span>
                                    <FormattedMessage {...messages.facebookRemove} />
                                </Submit>
                            ) : (
                                <Submit facebook disabled onClick={() => null}>
                                    <span className={"button-facebook"}>
                                        <Icon i="facebook" />
                                    </span>
                                    <FormattedMessage {...messages.facebookConnected} />
                                </Submit>
                            )
                        ) : (
                            <FacebookLogin
                                appId={facebookAppId}
                                fields={fields}
                                onClick={facebookLoginStarted}
                                disableMobileRedirect
                                render={renderProps => (
                                    <Cancel facebook onClick={renderProps.onClick as () => void} disabled={fbInProgress}>
                                        <span className={cn("button-facebook button-facebook--dark")}>
                                            <Icon i="facebook" />
                                        </span>
                                        {fbInProgress
                                            ? formatMessage(messages.facebookConnectingInProgress)
                                            : formatMessage(messages.facebookConnect)}
                                    </Cancel>
                                )}
                                callback={facebookConnect}
                            />
                        )}
                        {user.google?.connected ? (
                            user.password ? (
                                <Submit google onClick={removeGoogle} disabled={glInProgress}>
                                    <span className={"button-google"}>
                                        <Icon i="google" />
                                    </span>
                                    <FormattedMessage {...messages.googleRemove} />
                                </Submit>
                            ) : (
                                <Submit google disabled onClick={() => null}>
                                    <span className={"button-google"}>
                                        <Icon i="google" />
                                    </span>
                                    <FormattedMessage {...messages.googleConnected} />
                                </Submit>
                            )
                        ) : (
                            <Cancel google onClick={handleLoginWithGoogle} disabled={glInProgress}>
                                <span className={cn("button-google button-google--dark")}>
                                    <Icon i="google" />
                                </span>
                                {glInProgress
                                    ? formatMessage(messages.googleConnectingInProgress)
                                    : formatMessage(messages.googleConnect)}
                            </Cancel>
                        )}
                    </SocialConnections>
                )}

                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.email} />
                    </Label>
                    <Value>
                        {user.email || <FormattedMessage {...messages.notGiven} />}
                        {user.email && (
                            <VerifiedEmail verified={user.verifiedEmail}>
                                {user.verifiedEmail
                                    ? formatMessage(messages.verified)
                                    : formatMessage(messages.notVerified)}
                            </VerifiedEmail>
                        )}
                        <div className={"EditButton"} onClick={() => changeTextValue("email", user.email || "")}>
                            <Icon i="edit" />
                        </div>
                    </Value>
                    {user.email && !user.confirmEmail && !user.verifiedEmail && (
                        <ResendEmailValidation
                            verificationSuccess={verificationSuccess}
                            resendVerification={resendVerification}
                        />
                    )}
                </FormElement>
                {user.confirmEmail && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.emailToBeVerified} />
                        </Label>
                        <Value>
                            {user.confirmEmail}
                            <VerifiedEmail>
                                <FormattedMessage {...messages.notVerified} />
                            </VerifiedEmail>
                        </Value>
                        <ResendEmailValidation
                            verificationSuccess={verificationSuccess}
                            resendVerification={resendVerification}
                        />
                    </FormElement>
                )}
                {!user.isStudent && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.username} />
                        </Label>
                        <Value>
                            {user.username}
                            <div className={"EditButton"} onClick={() => changeTextValue("username", user.username)}>
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}
                {!user.isStudent && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.displayName} />
                        </Label>
                        <Value>
                            {
                                displayNames(formatMessage)[
                                    displayNames(formatMessage).findIndex(l => l.value === user.displayName)
                                ]?.label
                            }
                            <div
                                className={"EditButton"}
                                onClick={() => changeSelectValue("displayName", user.displayName)}
                            >
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}

                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.last_name} />
                    </Label>
                    <Value>
                        {user.last_name}
                        <div className={"EditButton"} onClick={() => changeTextValue("last_name", user.last_name)}>
                            <Icon i="edit" />
                        </div>
                    </Value>
                </FormElement>

                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.first_name} />
                    </Label>
                    <Value>
                        {user.first_name}
                        <div className={"EditButton"} onClick={() => changeTextValue("first_name", user.first_name)}>
                            <Icon i="edit" />
                        </div>
                    </Value>
                </FormElement>

                {!user.isStudent && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.birthyear} />
                        </Label>
                        <Value>
                            {user.birthyear || <FormattedMessage {...messages.notGiven} />}
                            <div className={"EditButton"} onClick={() => changeNumberValue("birthyear", user.birthyear)}>
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}

                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.language} />
                    </Label>
                    <Value>
                        {
                            languages(formatMessage)[languages(formatMessage).findIndex(l => l.value === user.language)]
                                .label
                        }
                        <div className={"EditButton"} onClick={() => changeSelectValue("language", user.language)}>
                            <Icon i="edit" />
                        </div>
                    </Value>
                </FormElement>
                
                {!user.isStudent && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.motherTongue} />
                        </Label>
                        <Value>
                            {(motherTongues.find(({ value }) => value === user.motherTongue) || { label: "N/A" }).label}
                            <div
                                className={"EditButton"}
                                onClick={() => changeSelectValue("motherTongue", user.motherTongue!)}
                            >
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}

                <FormElement>
                    <Label>
                        <FormattedMessage {...messages.gender} />
                    </Label>
                    <Value>
                        {genders(formatMessage)[genders(formatMessage).findIndex(g => g.value === user.gender)].label}
                        <div className={"EditButton"} onClick={() => changeRadioValue("gender", user.gender!)}>
                            <Icon i="edit" />
                        </div>
                    </Value>
                </FormElement>
                {isClassroomTeacher && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.work_since} />
                        </Label>
                        <Value>
                            {user.work_since || <FormattedMessage {...messages.notGiven} />}
                            <div
                                className={"EditButton"}
                                onClick={() => changeNumberValue("work_since", user.work_since!)}
                            >
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}
                {isClassroomTeacher && (
                    <FormElement>
                        <Label>
                            <FormattedMessage {...messages.school_types} />
                        </Label>
                        <Value>
                            {(
                                (user.school_types &&
                                    user.school_types.map(
                                        type => schoolTypes(formatMessage).find(t => t.value === type)!.label
                                    )) ||
                                []
                            ).join(", ") || <FormattedMessage {...messages.notGiven} />}
                            <div
                                className={"EditButton"}
                                onClick={() => changeCheckboxValue("school_types", user.school_types || [])}
                            >
                                <Icon i="edit" />
                            </div>
                        </Value>
                    </FormElement>
                )}

                {changeTextModal && (
                    <ChangeTextValue
                        close={closeChangeTextModal}
                        changingName={changingName}
                        changingValue={changingValue as string}
                        changingLabel={changingLabel}
                    />
                )}
                {changeNumberModal && (
                    <ChangeNumberValue
                        close={closeChangeNumberModal}
                        changingName={changingName}
                        changingValue={changingValue as string}
                        changingLabel={changingLabel}
                    />
                )}

                {changeSelectModal && (
                    <ChangeSelectValue
                        close={closeChangeSelectModal}
                        changingName={changingName}
                        changingValue={changingValue as string}
                        changingLabel={changingLabel}
                        options={options}
                    />
                )}
                {changeRadioModal && (
                    <ChangeRadioValue
                        close={closeChangeRadioModal}
                        changingName={changingName}
                        changingValue={changingValue as string}
                        changingLabel={changingLabel}
                        options={options}
                    />
                )}
                {changeCheckboxModal && (
                    <ChangeCheckboxValue
                        close={closeChangeCheckboxModal}
                        changingName={changingName}
                        changingValue={changingValue as string[]}
                        changingLabel={changingLabel}
                        options={options}
                    />
                )}
                {user.email && (
                    <FormElement>
                        {user.password ? (
                            <div className={"link"} onClick={setChangePassword}>
                                <FormattedMessage
                                    {...messages.modifySomething}
                                    values={{ value: formatMessage(messages.password) }}
                                />
                            </div>
                        ) : (
                            <div className={"link"} onClick={setNewPassword}>
                                <FormattedMessage {...messages.createPassword} />
                            </div>
                        )}
                    </FormElement>
                )}
                {newPassword && <NewPassword close={setNewPassword} />}
                {changePassword && <ChangePassword close={setChangePassword} />}
                <FormElement>
                    {user.password ? (
                        <div className={"link"} onClick={setDeleteAccount}>
                            <FormattedMessage {...messages.deleteAccount} />
                        </div>
                    ) : (
                        <div className={"link"}>
                            <FormattedMessage {...messages.deleteAccountCreatePassword} />
                        </div>
                    )}
                </FormElement>

                {deleteAccount && <DeleteAccountModal close={setDeleteAccount} />}
                {modalOpen && (
                    <Modal close={closeModal}>
                        <Title>
                            <FormattedMessage {...messages.facebookConnectionError} />
                        </Title>
                        <p>
                            {modalType === "fb already connected" && (
                                <span>
                                    <FormattedMessage {...messages.facebookAlreadyConnected} />
                                </span>
                            )}
                            {modalType === "fb connection error" && (
                                <span>
                                    <FormattedMessage {...messages.facebookConnectionErrorMessage} />
                                </span>
                            )}
                        </p>
                        <Cancel onClick={closeModal}>
                            <FormattedMessage {...messages.close} />
                        </Cancel>
                    </Modal>
                )}
            </InnerSpring>
        </StyledAccount>
    );
};

export default AccountView;
